import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Calendar, MapPin, Edit3, Trash2, Upload, X, Loader2, Check,
  Eye, EyeOff, Users, ExternalLink,
} from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { BACKEND_URL } from '@/config/api';
import { useConfirm } from '@/components/dashboard/admin/ConfirmDialog';
import { Badge } from '@/components/dashboard/kit';

// flyer_url comes from the API as a relative path (`/api/events/.../flyer`).
// Backend and frontend live on different hosts in production, so always prepend.
// `v` (updated_at) evita que el navegador muestre el flyer anterior tras reemplazarlo.
const absoluteFlyer = (url, v) => (url ? `${BACKEND_URL}${url}${v ? `${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(v)}` : ''}` : null);

/*
  EventsManager — admin UI para crear, editar, publicar y eliminar eventos.

  Diseño:
    - Tabla compacta con todos los eventos (cualquier estado)
    - Botón "Nuevo evento" abre un modal de creación
    - Click "editar" abre el modal en modo edición
    - El upload del flyer es una acción separada (PUT distinto al PATCH de datos)
    - «Ver inscritos» lleva a Personas › Inscripciones filtrado por este evento (con su pago y su ficha)

  Datos compartidos con el frontend público:
    Los eventos publicados aquí aparecen automáticamente en /eventos.
    Los borradores y archivados quedan invisibles para el público.
*/

const iconBtn = 'w-10 h-10 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gold hover:bg-white/[0.04]';

const STATUS_META = {
  draft:     { label: 'Borrador',    tone: 'neutral' },
  published: { label: 'Publicado',   tone: 'good' },
  archived:  { label: 'Archivado',   tone: 'neutral' },
};

const DEFAULT_FORM = {
  title: '',
  subtitle: '',
  slug: '',
  event_date: '',
  arrival_time: '17:30',
  start_time: '18:00',
  location_name: '',
  location_address: '',
  city: '',
  cocktail_enabled: true,
  cocktail_price_eur: 19,
  status: 'draft',
  // Masterclass en directo (02/11/2026): online por Zoom, landing /masterclass-en-directo
  format: 'presencial',
  online_url: '',
};

// Convert ISO datetime → format expected by <input type="datetime-local">
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  } catch { return ''; }
};

function EventFormModal({ event, onClose, onSaved }) {
  const isEditing = !!event;
  const [form, setForm] = useState(() => {
    if (!event) return DEFAULT_FORM;
    return {
      title: event.title || '',
      subtitle: event.subtitle || '',
      slug: event.slug || '',
      event_date: isoToLocalInput(event.event_date),
      arrival_time: event.arrival_time || '17:30',
      start_time: event.start_time || '18:00',
      location_name: event.location_name || '',
      location_address: event.location_address || '',
      city: event.city || '',
      cocktail_enabled: event.cocktail_enabled ?? true,
      cocktail_price_eur: event.cocktail_price_eur ?? 19,
      status: event.status || 'draft',
      format: event.format || 'presencial',
      online_url: event.online_url || '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.event_date) {
      toast.error('Título y fecha son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const online = form.format === 'online';
      const payload = {
        ...form,
        // Online: la hora de inicio es la del campo «Fecha y hora» (hora de España), sin cóctel ni lugar
        ...(online ? { start_time: form.event_date.slice(11, 16), arrival_time: form.event_date.slice(11, 16),
          cocktail_enabled: false, location_name: 'Online (Zoom)', location_address: '', city: '' } : {}),
        online_url: online ? form.online_url.trim() : '',
        // Re-serialise datetime-local back to ISO with seconds
        event_date: new Date(form.event_date).toISOString(),
        // Vacío = que lo genere el servidor (un slug vacío rompería la URL del evento)
        slug: form.slug.trim() || undefined,
      };
      const result = isEditing
        ? await dashboardApi.adminUpdateEvent(event.id, payload)
        : await dashboardApi.adminCreateEvent(payload);
      toast.success(isEditing ? 'Evento actualizado' : 'Evento creado');
      onSaved(result);
    } catch (err) {
      toast.error(err.message || 'Error guardando el evento');
    } finally {
      setSaving(false);
    }
  };

  const handleFlyerSelect = async (file) => {
    if (!file || !isEditing) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen excede 5 MB');
      return;
    }
    setUploading(true);
    try {
      await dashboardApi.adminUploadEventFlyer(event.id, file);
      toast.success('Flyer subido');
      // Re-fetch the event so the modal shows the new flyer
      const fresh = await dashboardApi.adminGetEvent(event.id);
      onSaved(fresh, { keepOpen: true });
    } catch (err) {
      toast.error(err.message || 'Error subiendo el flyer');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1829] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0d1829] z-10">
          <h3 className="text-white font-semibold text-base">
            {isEditing ? `Editar: ${event.title}` : 'Nuevo evento'}
          </h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Flyer upload — only when editing */}
          {isEditing && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-2 block font-semibold">Flyer del evento</label>
              <div className="flex items-center gap-4">
                {event.flyer_url ? (
                  <img src={absoluteFlyer(event.flyer_url, event.updated_at)} alt="Flyer actual" className="w-20 h-28 object-cover rounded border border-white/10" />
                ) : (
                  <div className="w-20 h-28 bg-white/[0.04] border border-white/10 rounded flex items-center justify-center text-[11px] text-gray-400 text-center">
                    Sin flyer
                  </div>
                )}
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleFlyerSelect(e.target.files[0])}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] text-sm text-white border border-white/10 disabled:opacity-50"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {event.flyer_url ? 'Reemplazar flyer' : 'Subir flyer'}
                  </button>
                  <p className="text-[11px] text-gray-400 mt-1.5">JPG, PNG o WebP · máx. 5 MB</p>
                </div>
              </div>
            </div>
          )}

          {/* Basic fields */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Título *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
              placeholder="Ej. El nuevo inmobiliario" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Subtítulo</label>
            <textarea value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              rows={2}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40 resize-none"
              placeholder="Descubre cómo funciona la tokenización inmobiliaria…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Slug (URL)</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40 font-mono"
                placeholder="amaroo-junio-2026" />
              <p className="text-[11px] text-gray-400 mt-1">Se genera solo si lo dejas vacío</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Fecha y hora *</label>
              <input type="datetime-local" value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Formato</label>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Formato del evento">
              {[['presencial', 'Presencial'], ['online', 'Masterclass online (Zoom)']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, format: k })} aria-pressed={form.format === k}
                  className={`rounded-lg border px-3 py-2 text-sm ${form.format === k ? 'border-[#c9a84c]/50 bg-[#c9a84c]/10 text-[#e3c87a]' : 'border-white/10 text-gray-300 hover:bg-white/[0.04]'}`}
                  data-testid={`event-format-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          {form.format === 'online' && (
            <div className="rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/[0.05] p-4 space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Enlace de Zoom</label>
              <input value={form.online_url} onChange={(e) => setForm({ ...form, online_url: e.target.value })}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
                placeholder="https://us06web.zoom.us/j/…" data-testid="event-online-url" />
              <p className="text-[11px] text-gray-400">
                Solo se envía por email a los inscritos (confirmación y recordatorios 24 h y 1 h antes); nunca sale en la web.
                La fecha y hora de arriba es la de inicio, en hora de España. Se apuntan desde freedomhomes.club/masterclass-en-directo
                con nombre y email.
              </p>
            </div>
          )}
          {form.format !== 'online' && (<>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Llegada</label>
              <input value={form.arrival_time} onChange={(e) => setForm({ ...form, arrival_time: e.target.value })}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
                placeholder="17:30" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Inicio</label>
              <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
                placeholder="18:00" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Nombre del lugar</label>
            <input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
              placeholder="Amaroo" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Dirección</label>
            <input value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
              placeholder="Partida El Runal 31, Busot" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Ciudad</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40"
              placeholder="Busot, Alicante" />
          </div>

          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.cocktail_enabled}
                onChange={(e) => setForm({ ...form, cocktail_enabled: e.target.checked })}
                className="h-4 w-4 rounded bg-white/[0.05] border-white/20 text-[#c9a84c]" />
              <span className="text-sm text-white">Habilitar opción «Con cóctel»</span>
            </label>
            {form.cocktail_enabled && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Precio cóctel (€)</label>
                <input type="number" min={0} max={1000} value={form.cocktail_price_eur}
                  onChange={(e) => setForm({ ...form, cocktail_price_eur: parseInt(e.target.value) || 0 })}
                  className="w-32 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40" />
              </div>
            )}
          </div>
          </>)}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold">Estado</label>
            <div className="flex gap-2">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <button key={key} type="button"
                  onClick={() => setForm({ ...form, status: key })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    form.status === key ? 'border-[#c9a84c]/40 bg-[#c9a84c]/10 text-[#c9a84c]' : 'border-white/10 bg-white/[0.02] text-gray-400'
                  }`}>
                  {meta.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-white/10 text-gray-400 hover:bg-white/[0.04] text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-[#c9a84c] hover:bg-[#d4b85c] disabled:opacity-50 text-[#0a1225] font-semibold text-sm inline-flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {isEditing ? 'Guardar' : 'Crear evento'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function EventsManager({ onShowRegistrations }) {
  const [dialog, ask] = useConfirm();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await dashboardApi.adminListEvents();
      setEvents(res.events || []);
    } catch (e) {
      toast.error(e.message || 'Error cargando eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleSaved = (saved, opts = {}) => {
    refresh();
    if (opts.keepOpen && saved) {
      setEditingEvent(saved); // refresh modal with latest data (incl. new flyer)
    } else {
      setEditingEvent(null);
      setCreating(false);
    }
  };

  const handleDelete = async (event) => {
    const ok = await ask({
      title: `Eliminar «${event.title}»`,
      details: [
        'Se borra el evento de la web y del panel de los miembros.',
        'Se borran también todas sus inscripciones. No se puede deshacer.',
      ],
      confirmLabel: 'Eliminar evento',
    });
    if (!ok) return;
    try {
      await dashboardApi.adminDeleteEvent(event.id);
      toast.success('Evento eliminado');
      refresh();
    } catch (e) {
      toast.error(e.message || 'Error eliminando');
    }
  };

  const handleToggleStatus = async (event) => {
    const next = event.status === 'published' ? 'draft' : 'published';
    try {
      await dashboardApi.adminUpdateEvent(event.id, { status: next });
      toast.success(next === 'published' ? 'Evento publicado' : 'Despublicado');
      refresh();
    } catch (e) {
      toast.error(e.message || 'Error');
    }
  };

  return (
    <div className="space-y-5">
      {dialog}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-white text-sm font-semibold">Eventos</h3>
          <p className="text-gray-400 text-xs mt-1">
            Los eventos publicados aparecen automáticamente en /eventos junto con los bootcamps.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c9a84c] hover:bg-[#d4b85c] text-[#0a1225] text-sm font-semibold"
          >
            <Plus size={14} /> Nuevo evento
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#c9a84c]" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] border border-dashed border-white/10 rounded-xl">
          <Calendar className="mx-auto text-gray-700 mb-3" size={32} />
          <p className="text-gray-400 text-sm">Aún no has creado ningún evento</p>
          <button onClick={() => setCreating(true)} className="mt-3 text-[#c9a84c] text-sm hover:underline">
            Crear el primero
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const meta = STATUS_META[event.status] || STATUS_META.draft;
            const eventDate = event.event_date ? new Date(event.event_date) : null;
            const isPast = eventDate && eventDate < new Date();
            return (
              <div key={event.id}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {event.flyer_url ? (
                    <img src={absoluteFlyer(event.flyer_url, event.updated_at)} alt="" className="w-12 h-16 object-cover rounded border border-white/10 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-16 bg-white/[0.04] rounded flex items-center justify-center flex-shrink-0">
                      <Calendar size={16} className="text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="text-white text-sm font-semibold truncate">{event.title}</h4>
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                      {isPast && <Badge>Pasado</Badge>}
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={11} />
                        {eventDate ? eventDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </span>
                      {event.city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} /> {event.city}
                        </span>
                      )}
                      <span className="font-mono text-[11px] text-gray-400">/{event.slug}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <a href={`/eventos/${event.slug}`} target="_blank" rel="noopener noreferrer"
                    title="Ver landing pública" aria-label={`Ver la página pública de ${event.title}`} className={iconBtn}>
                    <ExternalLink size={15} />
                  </a>
                  <button type="button" onClick={() => onShowRegistrations(`event:${event.slug}`)}
                    className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg border border-white/10 text-xs text-white hover:bg-white/[0.05]">
                    <Users size={13} /> Ver inscritos ({event.registrations_count || 0})
                  </button>
                  <button type="button" onClick={() => handleToggleStatus(event)} className={iconBtn}
                    title={event.status === 'published' ? 'Despublicar' : 'Publicar'} aria-label={event.status === 'published' ? `Despublicar ${event.title}` : `Publicar ${event.title}`}>
                    {event.status === 'published' ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button type="button" onClick={() => setEditingEvent(event)} title="Editar" aria-label={`Editar ${event.title}`} className={iconBtn}>
                    <Edit3 size={15} />
                  </button>
                  <button type="button" onClick={() => handleDelete(event)} title="Eliminar" aria-label={`Eliminar ${event.title}`}
                    className={`${iconBtn} hover:!text-red-400 hover:!bg-red-500/10`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(creating || editingEvent) && (
        <EventFormModal
          event={editingEvent}
          onClose={() => { setEditingEvent(null); setCreating(false); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
